<template>
  <div>
    <LoaderWithText v-if="isLoadingDetail">
      Loading case…
    </LoaderWithText>
    <div v-else-if="selectedCase">
      <div class="case-header">
        <Title>{{ caseSubjectLabel }}</Title>
        <Subtitle class="case-number-note tw-mb-2">Case number: {{ selectedCase.case_number || '-' }}</Subtitle>
        <Subtitle class="case-submitted-note tw-mb-2">Submitted: {{ submittedAtLabel }}</Subtitle>
        <Badges class="case-meta-badges" gap="2">
          <Badge :status="statusBadgeStatus" resizable>{{ selectedCase.status || '-' }}</Badge>
          <Badge :status="priorityBadgeStatus" resizable>{{ selectedCase.priority || '-' }}</Badge>
          <Badge resizable>{{ caseTypeLabel }}</Badge>
        </Badges>

      </div>
      <Callout
        v-if="attachmentError"
        class="attachment-alert"
        status="warning"
        icon-before="fas fa-exclamation-triangle"
      >
        {{ attachmentError }}
      </Callout>
      <div class="case-section submitted-request-section">
        <div class="submitted-request-header">
          <Subtitle>Submitted case details</Subtitle>
        </div>
        <div
          class="submitted-request-content"
          :class="{ 'submitted-request-content-collapsed': !isSubmissionDetailsExpanded }"
        >
          <CaseRequestFields
            :fields="caseRequestFields"
            mode="readonly"
            :show-only-populated="true"
            empty-state-text="No submitted request fields."
          />
          <div v-if="!isSubmissionDetailsExpanded" class="submitted-request-paywall">
            <button
              class="submitted-request-toggle-button"
              type="button"
              :aria-expanded="isSubmissionDetailsExpanded"
              aria-label="Show submitted case details"
              @click.stop="toggleSubmissionDetails"
            >
              <span>Click to expand submission details</span>
              <i class="fas fa-chevron-down"></i>
            </button>
          </div>
          <div v-else class="submitted-request-toggle-row">
            <button
              class="submitted-request-toggle-button"
              type="button"
              :aria-expanded="isSubmissionDetailsExpanded"
              aria-label="Hide submitted case details"
              @click.stop="toggleSubmissionDetails"
            >
              <span>Hide submission details</span>
              <i class="fas fa-chevron-up"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="case-section">
        <Subtitle class="stk-mb-2">Comments</Subtitle>
        <div v-if="comments.length === 0" class="comment-empty-not">No comments yet.</div>
        <div v-else class="comment-list-window">
          <div ref="commentList" class="comment-list">
            <div v-for="comment in comments" class="comment-item">
              <div class="comment-meta">
                <span>{{ comment.created_by || 'Advisor' }}</span>
                <span>{{ formatCommentTimestamp(comment.created_at) }}</span>
              </div>
              <div class="comment-body">{{ comment.body }}</div>
            </div>
          </div>
        </div>
        <Callout
          v-if="commentError"
          class="comment-alert"
          status="warning"
          icon-before="fas fa-exclamation-triangle"
        >
          {{ commentError }}
        </Callout>
        <CaseCommentAttachmentComposer
          v-if="allowCommentSubmission"
          :new-comment="newComment"
          :pending-attachment-files="[]"
          :is-submitting-comment="isSubmittingComment"
          :is-submitting-attachment="false"
          :allow-comment-submission="true"
          :allow-attachment-upload="false"
          :show-comment-submit-button="true"
          :show-attachment-submit-button="false"
          @update:new-comment="$emit('update:new-comment', $event)"
          @submit-comment="$emit('submit-comment')"
        />
      </div>
      <div v-if="allowAttachmentUpload" class="case-section">
        <Subtitle>Attachments</Subtitle>
        <CaseCommentAttachmentComposer
          :new-comment="''"
          :pending-attachment-files="pendingAttachmentFiles"
          :is-submitting-comment="false"
          :is-submitting-attachment="isSubmittingAttachment"
          :allow-comment-submission="false"
          :allow-attachment-upload="true"
          :show-comment-submit-button="false"
          :show-attachment-submit-button="true"
          :attachment-error="attachmentError"
          @update:pending-attachment-files="updatePendingAttachmentFiles"
          @submit-attachment="$emit('submit-attachment', $event)"
        />
        <div v-if="attachments.length === 0" class="attachment-empty-note">No attachments yet.</div>
        <div v-else>
          <div class="attachments-list-actions">
            <a
              v-if="attachmentsZipDownloadUrl"
              class="attachments-download-all-link"
              :href="attachmentsZipDownloadUrl"
            >
              Download all (.zip)
            </a>
          </div>
          <ul class="attachment-list existing-attachment-list">
          <li v-for="attachment in attachments" class="attachment-item">
            <div class="attachment-content">
              <a v-if="attachment.download_url" :href="attachment.download_url">
                {{ attachment.title || 'Attachment' }}
              </a>
              <span v-else>{{ attachment.title || 'Attachment' }}</span>
              <div class="attachment-meta">
                <span>{{ attachment.created_by || 'Advisor' }}</span>
                <span>{{ formatCommentTimestamp(attachment.created_at) }}</span>
              </div>
            </div>
          </li>
          </ul>
        </div>
      </div>
    </div>
    <div v-else class="empty-state">
      Select a case to view details.
    </div>
  </div>
</template>

<script>
import {
  Badge,
  Badges,
  Callout,
  LoaderWithText,
  Subtitle,
  Title,
} from '@stakeui';
import CaseCommentAttachmentComposer from '@frontend/ui/features/example/components/CaseCommentAttachmentComposer.vue';
import CaseRequestFields from '@frontend/ui/features/example/components/CaseRequestFields.vue';
import {
  getCaseTypeLabel,
  getCasePriorityBadgeStatus,
  getCaseStatusBadgeStatus,
} from '@frontend/ui/features/example/utils/caseBadges';

export default {
  name: 'CaseDetailPanel',
  components: {
    Badge,
    Badges,
    CaseCommentAttachmentComposer,
    CaseRequestFields,
    Callout,
    LoaderWithText,
    Subtitle,
    Title,
  },
  props: {
    selectedCase: {
      type: Object,
      default: null,
    },
    comments: {
      type: Array,
      default: () => [],
    },
    attachments: {
      type: Array,
      default: () => [],
    },
    isLoadingDetail: {
      type: Boolean,
      default: false,
    },
    isSubmittingComment: {
      type: Boolean,
      default: false,
    },
    isSubmittingAttachment: {
      type: Boolean,
      default: false,
    },
    attachmentError: {
      type: String,
      default: '',
    },
    commentError: {
      type: String,
      default: '',
    },
    newComment: {
      type: String,
      default: '',
    },
    allowCommentSubmission: {
      type: Boolean,
      default: true,
    },
    allowAttachmentUpload: {
      type: Boolean,
      default: true,
    },
    attachmentsZipDownloadUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isSubmissionDetailsExpanded: false,
      pendingAttachmentFiles: [],
    };
  },
  computed: {
    shouldUseDescriptionAsFallbackField() {
      const selectedCase = this.selectedCase || {};
      const submissionData = selectedCase.submitted_data;
      if (!submissionData || typeof submissionData !== 'object') {
        return false;
      }

      const normalizedSubject = String(submissionData.subject || '').trim();
      const normalizedType = String(submissionData.type || '').trim();
      const normalizedPriority = String(submissionData.priority || '').trim();
      const normalizedOrigin = String(submissionData.origin || '').trim();
      const normalizedStatus = String(submissionData.status || '').trim();
      const submissionFields = (
        submissionData.fields && typeof submissionData.fields === 'object'
      ) ? submissionData.fields : {};
      const hasSubmissionFields = Object.keys(submissionFields).length > 0;
      const hasOurSystemSubmissionSignature = [
        normalizedSubject,
        normalizedType,
        normalizedPriority,
        normalizedOrigin,
        normalizedStatus,
      ].some(Boolean) || hasSubmissionFields;
      if (hasOurSystemSubmissionSignature) {
        return false;
      }
      return true;
    },
    caseSubjectLabel() {
      const rawSubject = this.selectedCase?.subject ||
        this.selectedCase?.fields?.subject ||
        this.selectedCase?.submitted_data?.subject ||
        '';
      const subject = String(rawSubject).trim();
      if (!subject) {
        return 'Case';
      }
      const maxLength = 80;
      if (subject.length <= maxLength) {
        return subject;
      }
      return `${subject.slice(0, maxLength - 1)}...`;
    },
    caseRequestFields() {
      const fields = this.selectedCase?.fields || {};
      const fallbackSubject = this.selectedCase?.subject || this.selectedCase?.submitted_data?.subject || '';
      const fallbackDescription = this.shouldUseDescriptionAsFallbackField ?
        (this.selectedCase?.description || this.selectedCase?.submitted_data?.description || '') :
        '';
      return {
        ...fields,
        subject: fields.subject || fallbackSubject,
        description: fields.description || fallbackDescription,
      };
    },
    caseTypeLabel() {
      return getCaseTypeLabel(this.selectedCase);
    },
    submittedAtLabel() {
      return this.formatCommentTimestamp(this.selectedCase?.created_at);
    },
    statusBadgeStatus() {
      return getCaseStatusBadgeStatus(this.selectedCase?.status);
    },
    priorityBadgeStatus() {
      return getCasePriorityBadgeStatus(this.selectedCase?.priority);
    },
  },
  methods: {
    formatCommentTimestamp(value) {
      if (!value) {
        return '-';
      }
      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return value;
      }
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsedDate);
    },
    toggleSubmissionDetails() {
      this.isSubmissionDetailsExpanded = !this.isSubmissionDetailsExpanded;
    },
    updatePendingAttachmentFiles(nextFiles) {
      this.pendingAttachmentFiles = [...(nextFiles || [])];
    },
    scrollCommentsToBottom() {
      this.$nextTick(() => {
        const commentList = this.$refs.commentList;
        if (!commentList) {
          return;
        }
        commentList.scrollTop = commentList.scrollHeight;
      });
    },
  },
  watch: {
    isSubmittingAttachment(newValue, oldValue) {
      if (oldValue && !newValue && !this.attachmentError) {
        this.pendingAttachmentFiles = [];
      }
    },
    selectedCase(newCase, oldCase) {
      if (newCase?.id !== oldCase?.id) {
        this.pendingAttachmentFiles = [];
        this.scrollCommentsToBottom();
      }
    },
    comments() {
      this.scrollCommentsToBottom();
    },
  },
  mounted() {
    this.scrollCommentsToBottom();
  },
};
</script>

<style scoped>
.empty-state {
  color: var(--stk-color-text-muted);
  padding: var(--stk-size-4) 0;
}

.case-header {
  margin-bottom: var(--stk-size-4);
}

.case-meta-badges {
  margin-top: var(--stk-size-2);
}

.attachment-alert {
  margin-bottom: var(--stk-size-4);
}

.comment-alert {
  margin-bottom: var(--stk-size-3);
}

.case-number-note {
  font-size: 0.85rem;
  color: var(--stk-color-text-muted);
  margin: var(--stk-size-2) 0;
}

.case-submitted-note {
  font-size: 0.85rem;
  color: var(--stk-color-text-muted);
  margin: 0 0 var(--stk-size-2) 0;
}

.case-description {
  margin-bottom: var(--stk-size-4);
}

.case-section {
  margin-bottom: var(--stk-size-6);
}

.submitted-request-section {
  margin-bottom: 0;
}

.submitted-request-header {
  display: flex;
  align-items: center;
  margin-bottom: var(--stk-size-2);
}

.submitted-request-content {
  position: relative;
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
  padding: var(--stk-size-1) var(--stk-size-3) var(--stk-size-3);
}

.submitted-request-content-collapsed {
  max-height: 12rem;
  overflow: hidden;
}

.submitted-request-paywall {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: var(--stk-size-3);
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.2) 0%,
    rgba(255, 255, 255, 0.88) 70%,
    rgba(255, 255, 255, 0.98) 100%
  );
}

.submitted-request-toggle-row {
  display: flex;
  justify-content: center;
  padding-top: var(--stk-size-1);
}

.submitted-request-toggle-button {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  gap: var(--stk-size-2);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--stk-color-text-muted);
  background: var(--stk-color-white);
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
  padding: var(--stk-size-1) var(--stk-size-2);
}

.submitted-fields {
  display: grid;
  gap: var(--stk-size-3);
}

.form-radios {
  display: grid;
  gap: var(--stk-size-2);
}

.radios-wide {
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: var(--stk-size-3);
}

.case-readonly[disabled] {
  color: var(--stk-color-text-muted);
  background: var(--stk-color-background-soft);
  cursor: not-allowed;
}

.case-textarea {
  min-height: 6rem;
  resize: vertical;
}

.hint {
  color: var(--stk-color-text-muted);
  font-size: 0.85rem;
  margin-top: var(--stk-size-2);
}

.comment-list-window {
  position: relative;
  background: #fcfcfd;
  border-radius: 0.75rem;
  border: 1px solid #eceff3;
}

.comment-list {
  display: flex;
  flex-direction: column;
  max-height: 24rem;
  overflow-y: auto;
  gap: var(--stk-size-3);
  margin-bottom: var(--stk-size-3);
}

.comment-empty-note {
  font-size: 0.8rem;
  color: #a9adb8;
  margin-bottom: var(--stk-size-2);
}

.comment-item {
  padding: var(--stk-size-3);
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
}

.comment-meta {
  display: flex;
  gap: var(--stk-size-3);
  font-size: 0.8rem;
  color: var(--stk-color-text-muted);
  margin-bottom: var(--stk-size-2);
}

.attachment-list {
  list-style: none;
  margin: 0 0 var(--stk-size-3) 0;
  padding: 0;
}

.existing-attachment-list {
  margin-top: var(--stk-size-3);
}

.attachment-item {
  margin-bottom: var(--stk-size-2);
}

.attachment-content {
  display: flex;
  flex-direction: column;
  gap: var(--stk-size-1);
}

.attachment-meta {
  display: flex;
  gap: var(--stk-size-3);
  font-size: 0.8rem;
  color: var(--stk-color-text-muted);
}

.attachments-list-actions {
  display: flex;
  justify-content: flex-start;
}

.attachments-download-all-link {
  font-size: 0.8rem;
}

.attachment-empty-note {
  font-size: 0.8rem;
  color: #a9adb8;
  margin-bottom: var(--stk-size-2);
}
</style>
